import { useEffect, useRef, useState } from "react";
import { MdNavigateBefore, MdNavigateNext } from "react-icons/md";
import { Parallax } from "react-scroll-parallax";
import { Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/pagination";

import Review from "../../models/Review";
import FadeInComponent from "../ui/FadeInComponent";
import ReviewCard from "../review/ReviewCard";
import ElfsightWidget from "./ElfsightWidget";
import TrustmaryWidget from "./TrustmaryWidget";
import fallbackReviewsData from "../../others/reviews.json";

import styles from "./Testimonial.module.css";

interface GoogleReview {
  name?: string;
  rating: number;
  text?: {
    text?: string;
  };
  authorAttribution?: {
    displayName?: string;
    photoUri?: string;
    uri?: string;
  };
  publishTime?: string;
  googleMapsUri?: string;
}

interface GoogleReviewsJson {
  reviews: GoogleReview[];
}

const fallbackReviews: Review[] = (fallbackReviewsData as GoogleReviewsJson).reviews.map(
  (review, index) => ({
    id: review.name ?? String(index),
    source: "Google",
    rating: review.rating,
    profileImageUri: review.authorAttribution?.photoUri ?? "",
    authorName: review.authorAttribution?.displayName ?? "Google Reviewer",
    content: review.text?.text ?? "",
    date: review.publishTime
      ? new Date(review.publishTime).toLocaleDateString()
      : "",
    reviewUri: review.googleMapsUri ?? review.authorAttribution?.uri ?? "",
  })
);

function Testimonial() {
  const [reviews, setReviews] = useState<Review[]>(fallbackReviews);
  const [showTrustmaryWidget, setShowTrustmaryWidget] = useState(true);

  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchGoogleReviews = async () => {
      try {
        const response = await fetch("/.netlify/functions/fetch-google-reviews", {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch reviews: ${response.status}`);
        }

        const data: Review[] = await response.json();

        setReviews(data.length > 0 ? data : fallbackReviews);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        console.error("Error fetching Google reviews:", error);
        setReviews(fallbackReviews);
      }
    };

    fetchGoogleReviews();

    return () => controller.abort();
  }, []);

  const handlePrevious = () => {
    swiperRef.current?.slidePrev();
  };

  const handleNext = () => {
    swiperRef.current?.slideNext();
  };

  return (
    <section
      id="testimonial"
      className={`container-fluid row align-items-center justify-content-center p-4 g-0 parallax-wrapper ${styles.testimonial}`}
    >
      <Parallax
        speed={-25}
        easing="easeInOut"
        className="parallax-background"
        style={{
          backgroundImage: "url('/images/background/happy-customer-2.webp')",
        }}
      />

      <div
        className="overlay"
        style={{ backgroundColor: "rgba(var(--color-bg-1-rgb), 0.6)" }}
      />

      <FadeInComponent className="text-center my-heading px-4">
        <h2>Testimonials</h2>
        <p>
          Proof that flavor can overflow and seep into the reviews, because taste
          speaks louder than words.
        </p>
      </FadeInComponent>

      <div className={`row g-0 mt-4 ${styles.swiperWrapper}`}>
        <div className={`col-md-1 d-none d-md-flex ${styles.swiperNav}`}>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Previous testimonial"
            data-testid="testimonial-prev"
            onClick={handlePrevious}
          >
            <MdNavigateBefore />
          </button>
        </div>

        <div className="col-12 col-md-10">
          <Swiper
            modules={[Pagination]}
            onSwiper={(swiper: SwiperType) => {
              swiperRef.current = swiper;
            }}
            loop={reviews.length > 3}
            spaceBetween={30}
            pagination={{
              clickable: true,
              el: `.${styles.swiperPagination}`,
            }}
            breakpoints={{
              0: { slidesPerView: 1 },
              693: { slidesPerView: 2 },
              1049: { slidesPerView: 3 },
            }}
          >
            {reviews.map((review) => (
              <SwiperSlide
                key={review.id}
                className="d-flex justify-content-center align-items-center"
              >
                <ReviewCard review={review} />
              </SwiperSlide>
            ))}
          </Swiper>

          <div className={styles.swiperPagination}></div>
        </div>

        <div className={`col-md-1 d-none d-md-flex ${styles.swiperNav}`}>
          <button
            type="button"
            className={styles.navButton}
            data-testid="testimonial-next"
            aria-label="Next testimonial"
            onClick={handleNext}
          >
            <MdNavigateNext />
          </button>
        </div>
      </div>

      {showTrustmaryWidget ? (
        <TrustmaryWidget setShowTrustmaryWidget={setShowTrustmaryWidget} />
      ) : (
        <div className="col-10 col-md-10 py-4">
          <ElfsightWidget type="yelp" />
        </div>
      )}
    </section>
  );
}

export default Testimonial;